import { Box, FormControl, InputLabel, MenuItem, Select } from "@mui/material";
import { DateTime } from "luxon";
import { useRecordContext } from "react-admin";
import { useFormContext } from "react-hook-form";
import type { DrivingSchool } from "../providers/drivingSchoolsProvider";

export function ServiceTimeFilter(props: {
  minValue?: string; // Earliest available month in format "YYYY-MM"; defaults to the driving school's start month
  source?: "serviceTime";
  alwaysOn?: boolean;
}) {
  const { minValue: configuredMinValue } = props;
  const drivingSchool = useRecordContext<DrivingSchool>();
  const { setValue, getValues } = useFormContext();

  // Don't show the serviceTime filter if a student is selected ...
  const studentId = getValues("studentId");
  if (studentId) {
    return null;
  }

  const minValue = configuredMinValue ?? drivingSchool?.startDate.toFormat("yyyy-MM");
  if (!minValue) {
    throw new Error("minValue is required when no driving school is available");
  }

  const minValueMatch = minValue.match(/^(\d{4})-(\d{2})$/);
  if (!minValueMatch) {
    throw new Error("minValue must be in the format 'YYYY-MM'");
  }
  const startYear = Number(minValueMatch[1]);
  const startMonth = Number(minValueMatch[2]);

  const { year: currentYear, month: currentMonth } = DateTime.now();
  const startMonthId = startMonth.toString().padStart(2, "0");
  const currentMonthId = currentMonth.toString().padStart(2, "0");
  const startMonthValue = `${startYear}-${startMonthId}`;
  const currentMonthValue = `${currentYear}-${currentMonthId}`;

  const availableYears = Array.from({ length: currentYear - startYear + 1 }, (_, i) => currentYear - i);

  const allMonths = [
    { id: "01", name: "Januar" },
    { id: "02", name: "Februar" },
    { id: "03", name: "März" },
    { id: "04", name: "April" },
    { id: "05", name: "Mai" },
    { id: "06", name: "Juni" },
    { id: "07", name: "Juli" },
    { id: "08", name: "August" },
    { id: "09", name: "September" },
    { id: "10", name: "Oktober" },
    { id: "11", name: "November" },
    { id: "12", name: "Dezember" },
  ];

  const getAvailableMonths = () => {
    const selectedYear = getSelectedYear();
    if (selectedYear === `${currentYear}`) {
      return allMonths.slice(0, currentMonth);
    } else if (selectedYear === `${startYear}`) {
      return allMonths.slice(startMonth - 1);
    }
    return allMonths.slice();
  };

  const getSelectedYear = () => {
    const selection = getValues("serviceTime");
    return selection ? selection.split("-")[0] : currentYear;
  };

  const getSelectedMonth = () => {
    const selection = getValues("serviceTime");
    const hasMonth = selection && /-\d{2}$/.test(selection);
    return hasMonth ? selection.split("-")[1] : "";
  };

  const handleYearChange = (selectedYear: number | string) => {
    if (!selectedYear) {
      throw new Error("Missing value for selectedYear");
    }
    // Set to year-only format (YYYY) to show all months for that year
    setValue("serviceTime", `${selectedYear}`);
  };

  const handleMonthChange = (selectedYear: number | string, selectedMonth: string) => {
    if (!selectedYear) {
      throw new Error("Missing value for selectedYear");
    }

    // If no month is selected, set to year-only format
    if (!selectedMonth) {
      setValue("serviceTime", `${selectedYear}`);
      return;
    }

    const isCurrentYearSelected = `${selectedYear}` === `${currentYear}`;
    const isStartYearSelected = `${selectedYear}` === `${startYear}`;
    const isMonthAfterCurrent = Number(selectedMonth) > currentMonth;
    const isMonthBeforeStart = Number(selectedMonth) < startMonth;
    let newValue: string;

    // ensure a valid month is selected, since the selectable months may be limited in the first and current years
    if (isCurrentYearSelected && isMonthAfterCurrent) {
      newValue = currentMonthValue;
    } else if (isStartYearSelected && isMonthBeforeStart) {
      newValue = startMonthValue;
    } else {
      newValue = `${selectedYear}-${selectedMonth}`;
    }
    setValue("serviceTime", newValue);
  };

  return (
    <Box
      data-testid="ServiceTimeFilter"
      sx={{
        display: "grid",
        gap: { xs: 1, sm: 2 },
        gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))" },
        width: { xs: "100%", sm: "416px" },
      }}
    >
      <FormControl size="small" margin="dense" sx={{ minWidth: 0, width: "100% !important" }}>
        <InputLabel>Jahr</InputLabel>
        <Select
          label="Jahr"
          sx={{ minWidth: 0, width: "100%" }}
          onChange={(event) => {
            handleYearChange(event.target.value);
          }}
          value={getSelectedYear()}
        >
          {availableYears.map((year) => (
            <MenuItem key={year} value={year}>
              {year}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="small" margin="dense" sx={{ minWidth: 0, width: "100% !important" }}>
        <InputLabel>Monat (optional)</InputLabel>
        <Select
          label="Monat (optional)"
          sx={{ minWidth: 0, width: "100%" }}
          onChange={(event) => {
            handleMonthChange(getSelectedYear(), event.target.value);
          }}
          value={getSelectedMonth()}
        >
          <MenuItem value="">
            <em>Ganzes Jahr</em>
          </MenuItem>
          {getAvailableMonths().map(({ id, name }) => (
            <MenuItem key={id} value={id}>
              {name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
